import { makeObservable, observable, action, runInAction, computed } from 'mobx';
import notificationService from '../services/notificationService';

class NotificationStore {
  // Observable state
  notifications = [];
  unreadCount = 0;
  loading = false;
  loadingPreferences = false;
  error = null;

  // Preferences
  preferences = null;

  // Pagination
  currentPage = 1;
  totalCount = 0;
  hasMore = true;

  // Polling
  pollInterval = null;
  POLL_INTERVAL_MS = 30000; // 30 seconds

  constructor(rootStore) {
    this.rootStore = rootStore;

    makeObservable(this, {
      notifications: observable,
      unreadCount: observable,
      loading: observable,
      loadingPreferences: observable,
      error: observable,
      preferences: observable,
      currentPage: observable,
      totalCount: observable,
      hasMore: observable,
      unreadNotifications: computed,
      fetchNotifications: action,
      fetchUnreadCount: action,
      markAsRead: action,
      markAllAsRead: action,
      deleteNotification: action,
      fetchPreferences: action,
      updatePreferences: action,
      clearError: action,
      reset: action,
      startPolling: action,
      stopPolling: action
    });
  }

  /**
   * Get unread notifications
   */
  get unreadNotifications() {
    return this.notifications.filter(n => !n.is_read);
  }

  /**
   * Fetch notifications (paginated)
   * @param {Object} params - Query parameters (is_read, notification_type, page)
   * @param {boolean} append - Whether to append to existing or replace
   */
  async fetchNotifications(params = {}, append = false) {
    if (!append) {
      this.loading = true;
    }
    this.error = null;

    const result = await notificationService.fetchNotifications(params);

    runInAction(() => {
      if (result.success) {
        const newNotifications = result.data.results || result.data;
        if (append) {
          this.notifications = [...this.notifications, ...newNotifications];
        } else {
          this.notifications = newNotifications;
        }
        this.totalCount = result.data.count || newNotifications.length;
        this.hasMore = result.data.next !== null;
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result.success;
  }

  /**
   * Fetch unread count only (for polling)
   */
  async fetchUnreadCount() {
    const result = await notificationService.fetchUnreadCount();

    runInAction(() => {
      if (result.success) {
        this.unreadCount = result.data.unread_count;
      }
    });

    return result.success;
  }

  /**
   * Mark a notification as read
   * @param {number} notificationId
   */
  async markAsRead(notificationId) {
    const result = await notificationService.markAsRead(notificationId);

    runInAction(() => {
      if (result.success) {
        // Update the notification in the list
        const index = this.notifications.findIndex(n => n.id === notificationId);
        if (index !== -1) {
          this.notifications[index] = result.data;
        }
        // Decrement unread count
        if (this.unreadCount > 0) {
          this.unreadCount--;
        }
      } else {
        this.error = result.error;
      }
    });

    return result;
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead() {
    const result = await notificationService.markAllAsRead();

    runInAction(() => {
      if (result.success) {
        // Mark all notifications as read locally
        this.notifications = this.notifications.map(n => ({
          ...n,
          is_read: true,
          read_at: new Date().toISOString()
        }));
        this.unreadCount = 0;
      } else {
        this.error = result.error;
      }
    });

    return result;
  }

  /**
   * Delete a notification
   * @param {number} notificationId
   */
  async deleteNotification(notificationId) {
    const result = await notificationService.deleteNotification(notificationId);

    runInAction(() => {
      if (result.success) {
        // Check if notification was unread before removing
        const notification = this.notifications.find(n => n.id === notificationId);
        if (notification && !notification.is_read && this.unreadCount > 0) {
          this.unreadCount--;
        }
        // Remove from list
        this.notifications = this.notifications.filter(n => n.id !== notificationId);
      } else {
        this.error = result.error;
      }
    });

    return result;
  }

  /**
   * Fetch notification preferences
   */
  async fetchPreferences() {
    this.loadingPreferences = true;
    this.error = null;

    const result = await notificationService.fetchPreferences();

    runInAction(() => {
      if (result.success) {
        this.preferences = result.data;
      } else {
        this.error = result.error;
      }
      this.loadingPreferences = false;
    });

    return result;
  }

  /**
   * Update notification preferences
   * @param {Object} preferences
   */
  async updatePreferences(preferences) {
    this.loadingPreferences = true;
    this.error = null;

    const result = await notificationService.updatePreferences(preferences);

    runInAction(() => {
      if (result.success) {
        this.preferences = result.data;
      } else {
        this.error = result.error;
      }
      this.loadingPreferences = false;
    });

    return result;
  }

  /**
   * Start polling for unread count
   */
  startPolling() {
    // Clear any existing interval
    this.stopPolling();

    // Fetch immediately
    this.fetchUnreadCount();

    // Then poll periodically
    this.pollInterval = setInterval(() => {
      this.fetchUnreadCount();
    }, this.POLL_INTERVAL_MS);
  }

  /**
   * Stop polling
   */
  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  /**
   * Clear error message
   */
  clearError() {
    this.error = null;
  }

  /**
   * Reset store (on logout)
   */
  reset() {
    this.stopPolling();
    this.notifications = [];
    this.unreadCount = 0;
    this.loading = false;
    this.loadingPreferences = false;
    this.error = null;
    this.preferences = null;
    this.currentPage = 1;
    this.totalCount = 0;
    this.hasMore = true;
  }

  /**
   * Get notification icon based on type
   * @param {string} type
   */
  getNotificationIcon(type) {
    const icons = {
      ORDER_CREATED: 'shopping-cart',
      ORDER_CONFIRMED: 'check-circle',
      ORDER_SHIPPED: 'car',
      ORDER_DELIVERED: 'check-square',
      ORDER_CANCELLED: 'close-circle',
      APPOINTMENT_CREATED: 'calendar',
      APPOINTMENT_CONFIRMED: 'check-circle',
      APPOINTMENT_CANCELLED: 'close-circle',
      CATEGORY_APPROVED: 'check-circle',
      CATEGORY_REJECTED: 'close-circle',
      BREED_APPROVED: 'check-circle',
      BREED_REJECTED: 'close-circle',
      SUBSCRIPTION_EXPIRING: 'warning',
      NEW_REVIEW: 'star',
      REVIEW_RESPONSE: 'message',
      KYC_VERIFIED: 'safety-certificate',
      KYC_REJECTED: 'close-circle',
      SYSTEM: 'info-circle'
    };
    return icons[type] || 'bell';
  }

  /**
   * Get notification color based on priority
   * @param {string} priority
   */
  getNotificationColor(priority) {
    const colors = {
      LOW: '#8c8c8c',
      NORMAL: '#1890ff',
      HIGH: '#fa8c16',
      URGENT: '#ff4d4f'
    };
    return colors[priority] || colors.NORMAL;
  }
}

export default NotificationStore;
